import type {
	CoreApiError,
	IUserWritePayload,
	UserItem,
} from '@lqa-frontend/api-services';
import {
	coreApiClient,
	coreApiQueryKey,
	endpoints,
	HTTP_STATUS,
} from '@lqa-frontend/api-services';
import type { UseMutationOptions } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError, AxiosResponse } from 'axios';

export function useInvalidateAfterUserWrite() {
	const queryClient = useQueryClient();

	return async () => {
		await Promise.all([
			queryClient.invalidateQueries({
				queryKey: coreApiQueryKey(endpoints.userSettings.users.index, 'get'),
			}),
			queryClient.invalidateQueries({
				queryKey: coreApiQueryKey(endpoints.userSettings.users.item(''), 'get'),
			}),
			queryClient.invalidateQueries({
				queryKey: coreApiQueryKey(
					endpoints.myLQA.contracts.hotelOverview(''),
					'get'
				),
			}),
		]);
	};
}

type UseUserCreateMutationOptions = UseMutationOptions<
	AxiosResponse<UserItem>,
	AxiosError<CoreApiError>,
	IUserWritePayload
>;

export function useUserCreateMutation(options?: UseUserCreateMutationOptions) {
	const invalidateAfterWrite = useInvalidateAfterUserWrite();

	return useMutation({
		mutationKey: coreApiQueryKey(endpoints.userSettings.users.index, 'post'),
		mutationFn: async (payload) =>
			coreApiClient.post<UserItem>(endpoints.userSettings.users.index, payload),
		...options,
		onSuccess: async (data, variables, onMutateResult, context) => {
			await invalidateAfterWrite();
			options?.onSuccess?.(data, variables, onMutateResult, context);
		},
	});
}

export function useUserUpdateMutation(options?: UseUserUpdateMutationOptions) {
	const invalidateAfterWrite = useInvalidateAfterUserWrite();

	return useMutation({
		mutationKey: coreApiQueryKey(endpoints.userSettings.users.index, 'put'),
		mutationFn: async (rq) =>
			coreApiClient.put<UserItem>(
				endpoints.userSettings.users.item(rq.id),
				rq.data
			),
		...options,
		onSuccess: async (data, variables, onMutateResult, context) => {
			await invalidateAfterWrite();
			options?.onSuccess?.(data, variables, onMutateResult, context);
		},
	});
}

type UseUserUpdateMutationOptions = UseMutationOptions<
	AxiosResponse<UserItem>,
	AxiosError<CoreApiError>,
	IUserUpdateRequest
>;

interface IUserUpdateRequest {
	id: number;
	data: IUserWritePayload;
}

type UseUsersActivationMutationOptions = UseMutationOptions<
	AxiosResponse<void>,
	AxiosError<CoreApiError>,
	number[]
>;

export function useUsersActivateMutation(
	options?: UseUsersActivationMutationOptions
) {
	const invalidateAfterWrite = useInvalidateAfterUserWrite();

	return useMutation({
		mutationKey: coreApiQueryKey(endpoints.userSettings.users.activate, 'post'),
		mutationFn: async (ids) =>
			coreApiClient.post<void>(endpoints.userSettings.users.activate, { ids }),
		...options,
		onSuccess: async (data, variables, onMutateResult, context) => {
			await invalidateAfterWrite();
			options?.onSuccess?.(data, variables, onMutateResult, context);
		},
		onError: async (error, variables, onMutateResult, context) => {
			if (error.response?.status === HTTP_STATUS.FORBIDDEN) {
				await invalidateAfterWrite();
			}
			options?.onError?.(error, variables, onMutateResult, context);
		},
	});
}

export function useUsersDeactivateMutation(
	options?: UseUsersActivationMutationOptions
) {
	const invalidateAfterWrite = useInvalidateAfterUserWrite();

	return useMutation({
		mutationKey: coreApiQueryKey(
			endpoints.userSettings.users.deactivate,
			'post'
		),
		mutationFn: async (ids) =>
			coreApiClient.post<void>(endpoints.userSettings.users.deactivate, {
				ids,
			}),
		...options,
		onSuccess: async (data, variables, onMutateResult, context) => {
			await invalidateAfterWrite();
			options?.onSuccess?.(data, variables, onMutateResult, context);
		},
		onError: async (error, variables, onMutateResult, context) => {
			if (error.response?.status === HTTP_STATUS.FORBIDDEN) {
				await invalidateAfterWrite();
			}
			options?.onError?.(error, variables, onMutateResult, context);
		},
	});
}
